'use client';

import { FunctionComponent } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import classNames from 'classnames';
import { EditorColorScheme } from '@nx-frontend/shared/types/portal-colors';
import { getSkeletonColorsByTheme } from './getSkeletonColors';

export enum SkeletonType {
  BLOCKS = 'blocks',
  NOTE = 'note',
  INVITE_POPUP = 'invite-popup',
  WEBNOTES_NOTE = 'webnotes-note',
}

interface SkeletonWrapperProps {
  type: SkeletonType;
  editorColorScheme: EditorColorScheme;
  isFullWidth?: boolean;
}

export const SkeletonWrapper: FunctionComponent<SkeletonWrapperProps> = ({
  type,
  editorColorScheme,
  isFullWidth = false,
}) => {
  const colorsForTheme = getSkeletonColorsByTheme(editorColorScheme);

  // Check that the colors are found
  if (!colorsForTheme) {
    console.error(`Theme not found!`);
    return null;
  }

  const { baseColor, waveColor } = colorsForTheme;

  return (
    <SkeletonTheme baseColor={baseColor} highlightColor={waveColor}>
      {type === SkeletonType.BLOCKS && (
        <div data-cy="skeleton-element" className="w-full flex flex-wrap gap-6 p-4 md:p-4 lg:p-4">
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="lg:w-1/2 w-1/2" className="h-9" />
            <Skeleton containerClassName="lg:w-1/2 w-1/2" className="h-9" />
          </div>
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-16 md:h-36" />
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-16 md:h-36" />
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-16 md:h-36" />
          </div>
          <Skeleton containerClassName="w-full" className="h-7" />
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="w-1/2" className="h-24" />
            <Skeleton containerClassName="w-1/2" className="h-24" />
          </div>
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="lg:w-1/4 w-1/2" className="h-9" />
            <Skeleton containerClassName="lg:w-1/4 w-1/2" className="h-9" />
            <Skeleton containerClassName="lg:w-1/4 w-1/2" className="h-9" />
            <Skeleton containerClassName="lg:w-1/4 w-1/2" className="h-9" />
          </div>
          <Skeleton containerClassName="w-full" className="h-7" />
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-16 md:h-36" />
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-16 md:h-36" />
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-16 md:h-36" />
          </div>
          <Skeleton containerClassName="w-full" className="h-7" />
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="w-1/2" className="h-24" />
            <Skeleton containerClassName="w-1/2" className="h-24" />
          </div>
        </div>
      )}
      {type === SkeletonType.NOTE && (
        <div data-cy="skeleton-element-note" className="w-full flex flex-wrap gap-6 p-4 md:p-4 lg:p-4">
          <Skeleton containerClassName="lg:w-2/5 w-3/5" className="h-9" />
          <Skeleton containerClassName="w-full" className="h-7" />
          <Skeleton containerClassName="lg:w-2/3 w-4/5" className="h-7" />
          <Skeleton containerClassName="lg:w-2/3 w-full" className="h-7" />
          <Skeleton containerClassName="lg:w-3/4 w-4/5" className="h-7" />
          <Skeleton containerClassName="lg:w-full w-3/5" className="h-7" />
          <Skeleton containerClassName="lg:w-2/5 w-full" className="h-7" />
          <Skeleton containerClassName="lg:w-2/3 w-3/5" className="h-7" />
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-36" />
            <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-36" />
          </div>
          <Skeleton containerClassName="lg:w-3/4 w-full" className="h-7" />
        </div>
      )}
      {type === SkeletonType.WEBNOTES_NOTE && (
        <div data-cy="skeleton-element-note" className="w-full p-4 md:p-4 lg:p-4 pl-30 pr-40">
          <div
            className={classNames('w-full flex flex-wrap gap-6 mx-auto', {
              'max-w-1000': !isFullWidth,
              'px-4': isFullWidth,
            })}
          >
            <Skeleton containerClassName="lg:w-2/5 w-3/5" className="h-9" />
            <Skeleton containerClassName="w-full" className="h-7" />
            <Skeleton containerClassName="lg:w-2/3 w-4/5" className="h-7" />
            <Skeleton containerClassName="lg:w-2/3 w-full" className="h-7" />
            <Skeleton containerClassName="lg:w-3/4 w-4/5" className="h-7" />
            <Skeleton containerClassName="lg:w-full w-3/5" className="h-7" />
            <Skeleton containerClassName="lg:w-2/5 w-full" className="h-7" />
            <Skeleton containerClassName="lg:w-2/3 w-3/5" className="h-7" />
            <div className="w-full flex gap-6">
              <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-36" />
              <Skeleton containerClassName="lg:w-1/3 w-1/2" className="h-36" />
            </div>
            <Skeleton containerClassName="lg:w-3/4 w-full" className="h-7" />
          </div>
        </div>
      )}
      {type === SkeletonType.INVITE_POPUP && (
        <div data-cy="skeleton-element-note" className="w-full flex flex-wrap gap-6 p-4 md:p-4 lg:p-4">
          <Skeleton containerClassName="lg:w-2/3 w-4/5" className="h-7" />
          <Skeleton containerClassName="lg:w-2/5 w-3/5" className="h-9" />
          <div className="w-full flex gap-6">
            <Skeleton containerClassName="lg:w-2/3 w-4/5" className="h-7" />
            <Skeleton containerClassName="lg:w-2/3 w-4/5" className="h-7" />
          </div>
          <Skeleton containerClassName="lg:w-full w-3/5" className="h-8" />
          <Skeleton containerClassName="lg:w-full w-3/5" className="h-8" />
          <Skeleton containerClassName="lg:w-full w-3/5" className="h-8" />
          <Skeleton containerClassName="lg:w-full w-3/5" className="h-8" />
        </div>
      )}
    </SkeletonTheme>
  );
};
